import { CodeHighlightNode, CodeNode } from "@lexical/code";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { ListItemNode, ListNode } from "@lexical/list";
import {
	$convertFromMarkdownString,
	$convertToMarkdownString,
	TRANSFORMERS,
} from "@lexical/markdown";
import {
	AutoLinkPlugin,
	createLinkMatcherWithRegExp,
} from "@lexical/react/LexicalAutoLinkPlugin";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { type MutableRefObject, type ReactElement } from "react";
import "../styles/lexical-editor.css";
import { lexicalTheme } from "./lexicalTheme";

export interface LexicalEditorHandle {
	getMarkdown: () => string;
}

const URL_REGEX =
	/https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&/=]*)/;

const AUTO_LINK_MATCHERS = [
	createLinkMatcherWithRegExp(URL_REGEX, (text) => text),
];

const EDITOR_NODES = [
	HeadingNode,
	QuoteNode,
	ListNode,
	ListItemNode,
	LinkNode,
	AutoLinkNode,
	CodeNode,
	CodeHighlightNode,
];

interface RegisterHandleProps {
	handleRef: MutableRefObject<LexicalEditorHandle | null>;
}

function RegisterHandle({ handleRef }: RegisterHandleProps): null {
	const [editor] = useLexicalComposerContext();
	handleRef.current = {
		getMarkdown: () => {
			let markdown = "";
			editor.getEditorState().read(() => {
				markdown = $convertToMarkdownString(TRANSFORMERS);
			});
			return markdown;
		},
	};
	return null;
}

interface LexicalEditorProps {
	initialMarkdown: string;
	handleRef: MutableRefObject<LexicalEditorHandle | null>;
}

export function LexicalEditor({
	initialMarkdown,
	handleRef,
}: LexicalEditorProps): ReactElement {
	return (
		<LexicalComposer
			initialConfig={{
				namespace: "DocumentEditor",
				theme: lexicalTheme,
				nodes: EDITOR_NODES,
				onError: (error) => {
					console.error("Lexical error:", error);
				},
				editorState: () => {
					$convertFromMarkdownString(initialMarkdown, TRANSFORMERS);
				},
			}}
		>
			<div className="lexical-editor-container">
				<RichTextPlugin
					contentEditable={<ContentEditable />}
					ErrorBoundary={LexicalErrorBoundary}
				/>
				<MarkdownShortcutPlugin transformers={TRANSFORMERS} />
				<HistoryPlugin />
				<ListPlugin />
				<LinkPlugin />
				<AutoLinkPlugin matchers={AUTO_LINK_MATCHERS} />
				<RegisterHandle handleRef={handleRef} />
			</div>
		</LexicalComposer>
	);
}
